import { useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight, Plus } from 'lucide-react'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'
import type { QuickAction } from './navigation'

/*
 * The two floating buttons, rehoused.
 *
 * They sat over the content at the bottom right — over the pager on a long
 * list, over the last field of a form, over the Integracija token boxes in the
 * screenshot that started this. Nothing floats now: on a desk the actions are
 * a menu in the header, on a phone they are the centre of the bottom bar, at
 * the thumb, where the report button was.
 *
 * The colours stay. The report is the brand blue and the 5S walk the 5S green,
 * as the buttons were, so somebody who learnt "green is 5S" finds it again.
 */
const TONE: Record<QuickAction['id'], string> = {
  submission: 'bg-primary/12 text-primary dark:bg-primary/20',
  walk: 'bg-five-s/15 text-five-s',
  task: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
}

function ActionIcon({ action, className }: { action: QuickAction; className?: string }) {
  const Icon = action.icon

  return (
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-lg',
        TONE[action.id],
        className,
      )}
      aria-hidden
    >
      <Icon className="size-4" />
    </span>
  )
}

/** "+ Novo" in the desktop header. */
export function NewMenu({ actions }: { actions: QuickAction[] }) {
  const t = useT()
  const navigate = useNavigate()

  if (actions.length === 0) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button className="gap-1.5 pr-2.5 pl-3" />}>
        <Plus className="size-4" aria-hidden />
        {t('quick.new')}
        <ChevronDown className="size-3.5 opacity-70" aria-hidden />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={6} className="w-72">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.id}
            onClick={() => navigate(action.to)}
            className="items-center gap-2.5 py-1.5"
          >
            <ActionIcon action={action} />
            <span className="min-w-0">
              <span className="block font-medium">{t(action.labelKey)}</span>
              <span className="text-muted-foreground block text-xs">{t(action.hintKey)}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The same actions as a sheet from the bottom edge, for the phone's "+".
 *
 * A sheet rather than a dropdown: a menu anchored to a button in the bottom
 * bar opens upward into a narrow column, while a sheet gives each action the
 * full width of the screen and a row tall enough for a gloved thumb.
 */
export function QuickActionSheet({
  actions,
  open,
  onOpenChange,
}: {
  actions: QuickAction[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const t = useT()
  const navigate = useNavigate()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className={cn(
          'gap-0 rounded-t-3xl border-black/5 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-white/10',
          'supports-[backdrop-filter]:bg-popover/85 backdrop-blur-2xl backdrop-saturate-150',
        )}
      >
        <div className="flex justify-center pt-2" aria-hidden>
          <span className="bg-muted-foreground/30 h-1 w-9 rounded-full" />
        </div>

        <SheetHeader className="px-4 pt-2 pb-2">
          <SheetTitle>{t('quick.sheetTitle')}</SheetTitle>
          {/* Silences Base UI's missing-description warning without saying
              anything the rows below do not already say. */}
          <SheetDescription className="sr-only">{t('quick.new')}</SheetDescription>
        </SheetHeader>

        <ul className="space-y-1 px-2">
          {actions.map((action) => (
            <li key={action.id}>
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false)
                  navigate(action.to)
                }}
                className={cn(
                  'flex min-h-14 w-full items-center gap-3 rounded-2xl px-2.5 py-2 text-left transition-colors',
                  'hover:bg-muted active:bg-muted focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none',
                )}
              >
                <ActionIcon action={action} className="size-10 rounded-xl [&_svg]:size-5" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{t(action.labelKey)}</span>
                  <span className="text-muted-foreground block text-sm">{t(action.hintKey)}</span>
                </span>
                <ChevronRight className="text-muted-foreground size-4 shrink-0" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  )
}
